import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useConfig, waLink } from "@/context/ConfigContext";
import { useSeo } from "@/lib/useSeo";
import { SectionHeading } from "@/components/site/PageHero";
import { SahyadriDivider, WarliBand } from "@/components/site/Motifs";
import { AwardsSection } from "@/components/site/AwardsSection";
import { Button } from "@/components/ui/button";
import {
  MapPin, Route, LifeBuoy, Lock, Headset, ShieldCheck, ArrowRight, MessageCircle,
  MapPinned, Building2, CheckCircle2, Quote, Award,
} from "lucide-react";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1680348076991-31dd4654f005?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  "https://images.unsplash.com/photo-1712186870325-00427d06341a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  "https://images.unsplash.com/photo-1695453461885-bcecb1c64320?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
];

const BENEFIT_ICONS = { "map-pin": MapPin, route: Route, "life-buoy": LifeBuoy, lock: Lock, headset: Headset, "shield-check": ShieldCheck };
const TRUST_ICONS = { "map-pinned": MapPinned, "shield-check": ShieldCheck, route: Route, headset: Headset, "building-2": Building2 };

function CtaLink({ cta, wrapClass, btnClass, testid, size, variant, children }) {
  if (!cta || cta.enabled === false || !(cta.label || "").trim() || !(cta.href || "").trim()) return null;
  const btn = <Button data-testid={testid} size={size} variant={variant} className={btnClass}>{children || cta.label}</Button>;
  return cta.href.startsWith("/")
    ? <Link to={cta.href} className={wrapClass}>{btn}</Link>
    : <a href={cta.href} target="_blank" rel="noopener noreferrer" className={wrapClass}>{btn}</a>;
}

export default function Home() {
  useSeo({
    title: "Maha Sampada | Maharashtra's Premium B2B Destination Management Partner",
    description: "Maha Sampada is a Maharashtra DMC helping travel agents and tour operators create customized Maharashtra holidays with confidential B2B rates and reliable ground operations.",
  });
  const config = useConfig();
  const [heroIdx, setHeroIdx] = useState(0);
  const [sections, setSections] = useState(null);

  useEffect(() => { api.get("/cms/home").then((r) => setSections(r.data || [])).catch(() => setSections([])); }, []);

  const hero = (sections || []).find((s) => s.kind === "hero");
  const heroImages = (hero?.images && hero.images.length ? hero.images : HERO_IMAGES);
  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroImages.length), 5500);
    return () => clearInterval(t);
  }, [heroImages.length]);

  if (sections === null) return <div className="min-h-screen" data-testid="home-loading" />;

  const renderers = {
    hero: (s) => (
      <section key="hero" data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
        {heroImages.map((src, i) => (
          <img key={i} src={src} alt="Maharashtra landscape" className="absolute inset-0 h-full w-full object-cover transition-opacity duration-1000" style={{ opacity: i === heroIdx ? 1 : 0 }} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/85 via-charcoal/55 to-charcoal/20" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pt-28 sm:px-6">
          <div className="max-w-2xl fade-up">
            {s.overline && <div className="overline mb-4 text-gold">{s.overline}</div>}
            <h1 className="font-display text-4xl font-bold leading-[1.03] text-cream text-shadow-lg sm:text-6xl lg:text-7xl">{s.heading}</h1>
            {s.body && <p className="mt-6 max-w-xl text-base leading-relaxed text-cream/90 sm:text-lg">{s.body}</p>}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4">
              <CtaLink cta={s.cta} wrapClass="w-full sm:w-auto" testid="hero-query-cta" size="lg" btnClass="w-full rounded-full bg-saffron px-7 font-accent font-semibold text-white hover:bg-saffron-dark sm:w-auto" />
              <CtaLink cta={s.cta2} wrapClass="w-full sm:w-auto" testid="hero-partner-cta" size="lg" variant="outline" btnClass="w-full rounded-full border-cream bg-transparent px-7 font-accent font-semibold text-cream hover:bg-cream hover:text-charcoal sm:w-auto" />
            </div>
            {s.footnote && <p className="mt-8 font-accent text-xs tracking-wide text-cream/75">{s.footnote}</p>}
          </div>
        </div>
        <WarliBand className="absolute bottom-0 left-0 h-8 w-full opacity-70" color="#F7F1E7" />
      </section>
    ),
    categories: (s) => (
      <section key="categories" data-testid="categories-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <SectionHeading overline={s.overline} title={s.heading} description={s.body} />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(s.records || []).map((c, i) => (
            <Link key={c.slug || i} to={c.link || "/destinations"} data-testid={`category-card-${c.slug || i}`} className={`img-zoom hover-lift group relative block overflow-hidden rounded-2xl ${i === 0 ? "lg:col-span-2" : ""}`}>
              <div className={`${i === 0 ? "h-72" : "h-64"} w-full`}><img src={c.image} alt={c.title} className="h-full w-full object-cover" /></div>
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/30 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <h3 className="font-display text-2xl font-semibold text-cream">{c.title}</h3>
                <p className="mt-1 max-w-md text-sm text-cream/80">{c.description}</p>
                <span className="mt-3 inline-flex items-center gap-1 font-accent text-xs font-semibold text-gold">Explore <ArrowRight className="h-3 w-3" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    ),
    intro: (s) => (
      <section key="intro" data-testid="intro-section" className="bg-white paper-grain">
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-2">
          {s.image && <div className="arch-soft h-[460px] w-full img-zoom overflow-hidden"><img src={s.image} alt={s.image_alt || ""} className="h-full w-full object-cover" /></div>}
          <div>
            <SectionHeading overline={s.overline} title={s.heading} />
            {s.body && <p className="mt-6 text-base leading-relaxed text-muted-foreground">{s.body}</p>}
            <CtaLink cta={s.cta} wrapClass="mt-8 inline-block" testid="intro-cta" btnClass="rounded-full bg-maroon px-7 font-accent font-semibold text-white hover:bg-maroon/90" />
          </div>
        </div>
      </section>
    ),
    benefits: (s) => (
      <section key="why" data-testid="why-partner-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <SectionHeading overline={s.overline} title={s.heading} center />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {(s.benefits || []).map((b) => {
            const Icon = BENEFIT_ICONS[b.icon] || CheckCircle2;
            return (
              <div key={b.title} data-testid={`benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`} className="hover-lift rounded-2xl border border-gold/25 bg-white p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-saffron/10 text-saffron"><Icon className="h-6 w-6" /></div>
                <h3 className="mt-5 font-display text-xl font-semibold text-charcoal">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.text}</p>
              </div>
            );
          })}
          {s.agent_protection && (
            <div data-testid="benefit-agent-protection" className="relative overflow-hidden rounded-2xl bg-forest p-8 text-cream">
              <ShieldCheck className="absolute -right-4 -top-4 h-28 w-28 text-white/10" />
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-cream/15 text-gold"><ShieldCheck className="h-6 w-6" /></div>
              <h3 className="mt-5 font-display text-xl font-semibold">{s.agent_protection.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-cream/85">{s.agent_protection.text}</p>
            </div>
          )}
        </div>
      </section>
    ),
    itineraries: (s) => (
      <section key="featured" data-testid="featured-itineraries-section" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHeading overline={s.overline} title={s.heading} description={s.body} />
            {s.cta && s.cta.href && <Link to={s.cta.href}><Button variant="outline" className="rounded-full border-charcoal font-accent hover:bg-charcoal hover:text-white">{s.cta.label}</Button></Link>}
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {(s.records || []).map((it) => (
              <div key={it.slug} data-testid={`itinerary-card-${it.slug}`} className="hover-lift flex flex-col overflow-hidden rounded-2xl border border-gold/25 bg-cream">
                <div className="img-zoom h-44 overflow-hidden"><img src={it.cover_image} alt={it.title} className="h-full w-full object-cover" /></div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="overline text-saffron">{it.duration}</div>
                  <h3 className="mt-2 font-display text-xl font-semibold leading-tight text-charcoal">{it.title}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{(it.destinations || []).map((d) => (typeof d === "string" ? d : d.name)).join(" · ")}</p>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground line-clamp-3">{(() => { const el = typeof document !== "undefined" ? document.createElement("div") : null; if (!el) return (it.snapshot || "").replace(/<[^>]*>/g, ""); el.innerHTML = it.snapshot || ""; return el.textContent || ""; })()}</p>
                  <div className="mt-4 flex flex-col gap-2">
                    <Link to={`/itineraries/${it.slug}`}><Button size="sm" variant="outline" data-testid={`view-itinerary-${it.slug}`} className="w-full rounded-full border-charcoal font-accent text-xs hover:bg-charcoal hover:text-white">View Itinerary</Button></Link>
                    <Link to={`/enquiry?ref=${encodeURIComponent(it.title)}`}><Button size="sm" data-testid={`quote-itinerary-${it.slug}`} className="w-full rounded-full bg-saffron font-accent text-xs text-white hover:bg-saffron-dark">Request B2B Quote</Button></Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
    steps: (s) => (
      <section key="how" data-testid="how-it-works-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <SectionHeading overline={s.overline} title={s.heading} center />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {(s.steps || []).map((step) => (
            <div key={step.n} data-testid={`step-${step.n}`} className="relative rounded-2xl border border-gold/25 bg-white p-8">
              <div className="font-display text-5xl font-bold text-saffron/25">{step.n}</div>
              <h3 className="mt-3 font-display text-xl font-semibold text-charcoal">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.text}</p>
            </div>
          ))}
        </div>
      </section>
    ),
    destinations: (s) => (
      <section key="regions" data-testid="regions-section" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <SectionHeading overline={s.overline} title={s.heading} description={s.body} />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(s.records || []).map((r) => (
              <Link key={r.slug} to={`/destinations/${r.slug}`} data-testid={`region-card-${r.slug}`} className="img-zoom hover-lift group relative block h-56 overflow-hidden rounded-2xl">
                <img src={r.image} alt={r.name} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <h3 className="font-display text-lg font-semibold text-cream">{r.name}</h3>
                  <span className="mt-1 inline-flex items-center gap-1 font-accent text-[11px] text-gold opacity-0 transition-opacity group-hover:opacity-100">View region <ArrowRight className="h-3 w-3" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    ),
    banner: (s) => (
      <section key="banner" data-testid="partner-banner" className="relative overflow-hidden bg-maroon">
        <SahyadriDivider className="absolute left-0 top-0 h-12 w-full" color="#C69A4B" />
        <div className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 sm:py-24">
          {s.overline && <div className="overline mb-4 text-gold">{s.overline}</div>}
          <h2 className="font-display text-3xl font-bold leading-tight text-cream sm:text-5xl">{s.heading}</h2>
          {s.body && <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-cream/85">{s.body}</p>}
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <CtaLink cta={s.cta} wrapClass="w-full sm:w-auto" testid="partner-banner-cta" size="lg" btnClass="w-full rounded-full bg-saffron px-8 font-accent font-semibold text-white hover:bg-saffron-dark sm:w-auto" />
            <CtaLink cta={s.cta2} wrapClass="w-full sm:w-auto" testid="partner-banner-provider-cta" size="lg" variant="outline" btnClass="w-full rounded-full border-cream bg-transparent px-8 font-accent font-semibold text-cream hover:bg-cream hover:text-maroon sm:w-auto" />
          </div>
        </div>
      </section>
    ),
    trust: (s) => (
      <section key="trust" data-testid="trust-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <SectionHeading overline={s.overline} title={s.heading} center />
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          {(s.records || []).map((t) => {
            const Icon = TRUST_ICONS[t.icon] || CheckCircle2;
            return (
              <div key={t.title} data-testid="trust-indicator" className="flex items-center gap-3 rounded-full border border-gold/30 bg-white px-5 py-3">
                <Icon className="h-5 w-5 text-forest" />
                <span className="font-accent text-sm font-medium text-charcoal">{t.title}</span>
              </div>
            );
          })}
        </div>
      </section>
    ),
    testimonials: (s) => (
      <section key="testimonials" data-testid="testimonials-section" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <SectionHeading overline={s.overline} title={s.heading} center />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {(s.records || []).map((t, i) => (
              <div key={i} data-testid="testimonial-card" className="rounded-2xl border border-gold/25 bg-cream p-8">
                <Quote className="h-8 w-8 text-saffron/40" />
                <p className="mt-4 text-sm italic leading-relaxed text-charcoal">"{t.quote}"</p>
                <div className="mt-5 border-t border-gold/20 pt-4">
                  <div className="flex items-center justify-between">
                    <div className="font-accent text-sm font-semibold text-charcoal">{t.author}{t.verified && <span title="Verified source" className="ml-1 text-forest">✓</span>}</div>
                    {t.rating ? <div className="text-xs text-saffron" aria-label={`${t.rating} out of 5`}>{"★".repeat(Math.round(t.rating))}{"☆".repeat(5 - Math.round(t.rating))}</div> : null}
                  </div>
                  <div className="text-xs text-muted-foreground">{t.location}{t.review_date ? ` · ${t.review_date}` : ""}</div>
                  {t.source_url && t.source_provider !== "direct" && <a href={t.source_url} target="_blank" rel="noopener noreferrer nofollow" data-testid="testimonial-source-link" className="mt-1 inline-block text-[11px] font-semibold text-saffron hover:underline">View original review{t.source_provider ? ` on ${t.source_provider.charAt(0).toUpperCase() + t.source_provider.slice(1)}` : ""} →</a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
    awards: (s) => (
      <section key="awards" data-testid="awards-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <SectionHeading overline={s.overline} title={s.heading} center />
        <div className="mt-10 flex flex-wrap items-center justify-center gap-8">
          {(s.records || []).map((a, i) => (
            <div key={i} data-testid="award-item" className="flex flex-col items-center gap-2 text-center">
              {a.logo ? <img src={a.logo} alt={a.title} className="h-16 w-auto object-contain" /> : <Award className="h-12 w-12 text-gold" />}
              <span className="font-accent text-sm font-medium text-charcoal">{a.title}</span>
            </div>
          ))}
        </div>
      </section>
    ),
    cta: (s) => (
      <section key="finalcta" data-testid="final-cta-section" className="relative overflow-hidden bg-forest">
        <SahyadriDivider className="absolute left-0 top-0 h-12 w-full" color="#C69A4B" />
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-24">
          <h2 className="font-display text-3xl font-bold leading-tight text-cream sm:text-5xl">{s.heading}</h2>
          {s.body && <p className="mx-auto mt-5 max-w-xl text-base text-cream/85">{s.body}</p>}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CtaLink cta={s.cta} testid="final-query-cta" size="lg" btnClass="rounded-full bg-saffron px-8 font-accent font-semibold text-white hover:bg-saffron-dark" />
            {s.show_whatsapp && waLink(config.whatsapp) && (
              <a href={waLink(config.whatsapp)} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" data-testid="final-whatsapp-cta" className="rounded-full border-cream bg-transparent px-8 font-accent font-semibold text-cream hover:bg-cream hover:text-forest">
                  <MessageCircle className="mr-2 h-5 w-5" />Chat on WhatsApp
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>
    ),
  };

  return <div>{sections.map((s) => (renderers[s.kind] ? renderers[s.kind](s) : null))}<AwardsSection surface="homepage" /></div>;
}
